import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

const STORAGE_KEY = "parametric-ai-window-bounds-v1";
const EDGE_GAP = 10;
const MIN_WIDTH = 350;
const MIN_HEIGHT = 360;
const MAX_WIDTH = 760;

const clamp = (value, minimum, maximum) =>
  Math.min(Math.max(value, minimum), Math.max(minimum, maximum));

const getInitialBounds = () => {
  if (typeof window === "undefined") {
    return { x: 486, y: 72, width: 410, height: 620 };
  }

  try {
    const saved = JSON.parse(window.sessionStorage.getItem(STORAGE_KEY));
    if (
      saved &&
      ["x", "y", "width", "height"].every((key) =>
        Number.isFinite(saved[key])
      )
    ) {
      return saved;
    }
  } catch (_) {
    // Ignore an invalid saved window preference.
  }

  const width = Math.min(410, window.innerWidth - EDGE_GAP * 2);
  const preferredX = 40 + 430 + 16;
  const x = clamp(preferredX, EDGE_GAP, window.innerWidth - width - EDGE_GAP);
  const y = 72;
  const height = Math.max(
    MIN_HEIGHT,
    Math.min(620, window.innerHeight - y - EDGE_GAP)
  );

  return { x, y, width, height };
};

const fitBoundsToViewport = (bounds) => {
  if (typeof window === "undefined") return bounds;

  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const width = clamp(
    bounds.width,
    Math.min(MIN_WIDTH, viewportWidth - EDGE_GAP * 2),
    Math.min(MAX_WIDTH, viewportWidth - EDGE_GAP * 2)
  );
  const height = clamp(
    bounds.height,
    Math.min(MIN_HEIGHT, viewportHeight - 58 - EDGE_GAP),
    viewportHeight - 58 - EDGE_GAP
  );

  return {
    x: clamp(bounds.x, EDGE_GAP, viewportWidth - width - EDGE_GAP),
    y: clamp(bounds.y, 50, viewportHeight - 44 - EDGE_GAP),
    width,
    height,
  };
};

const FloatingParametricAiWindow = ({
  open,
  targetName,
  onClose,
  children,
}) => {
  const [bounds, setBounds] = useState(getInitialBounds);
  const [minimized, setMinimized] = useState(false);
  const interactionRef = useRef(null);

  useEffect(() => {
    const fitWindow = () => setBounds((current) => fitBoundsToViewport(current));
    fitWindow();
    window.addEventListener("resize", fitWindow);
    return () => window.removeEventListener("resize", fitWindow);
  }, []);

  useEffect(() => {
    try {
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(bounds));
    } catch (_) {
      // Window preferences are optional and never affect the design session.
    }
  }, [bounds]);

  useEffect(() => {
    if (open) return;

    const interaction = interactionRef.current;
    if (interaction?.moveHandler) {
      window.removeEventListener("pointermove", interaction.moveHandler);
    }
    if (interaction?.endHandler) {
      window.removeEventListener("pointerup", interaction.endHandler);
      window.removeEventListener("pointercancel", interaction.endHandler);
    }
    interactionRef.current = null;
    document.body.style.userSelect = "";
    document.body.style.cursor = "";
    setMinimized(false);
  }, [open]);

  useEffect(() => {
    return () => {
      const interaction = interactionRef.current;
      if (interaction?.moveHandler) {
        window.removeEventListener("pointermove", interaction.moveHandler);
      }
      if (interaction?.endHandler) {
        window.removeEventListener("pointerup", interaction.endHandler);
        window.removeEventListener("pointercancel", interaction.endHandler);
      }
      interactionRef.current = null;
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
    };
  }, []);

  if (typeof document === "undefined") return null;

  const stopInteraction = () => {
    const interaction = interactionRef.current;
    interactionRef.current = null;
    document.body.style.userSelect = "";
    document.body.style.cursor = "";
    if (interaction?.moveHandler) {
      window.removeEventListener("pointermove", interaction.moveHandler);
    }
    if (interaction?.endHandler) {
      window.removeEventListener("pointerup", interaction.endHandler);
      window.removeEventListener("pointercancel", interaction.endHandler);
    }
  };

  const handlePointerMove = (event) => {
    const interaction = interactionRef.current;
    if (!interaction) return;

    const dx = event.clientX - interaction.pointerX;
    const dy = event.clientY - interaction.pointerY;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const start = interaction.bounds;

    if (interaction.mode === "drag") {
      setBounds({
        ...start,
        x: clamp(start.x + dx, EDGE_GAP, viewportWidth - start.width - EDGE_GAP),
        y: clamp(start.y + dy, 50, viewportHeight - 44 - EDGE_GAP),
      });
      return;
    }

    let next = { ...start };

    if (interaction.mode.includes("left")) {
      const maximumWidth = Math.min(MAX_WIDTH, start.x + start.width - EDGE_GAP);
      next.width = clamp(start.width - dx, MIN_WIDTH, maximumWidth);
      next.x = start.x + start.width - next.width;
    }

    if (interaction.mode.includes("right")) {
      const maximumWidth = Math.min(
        MAX_WIDTH,
        viewportWidth - start.x - EDGE_GAP
      );
      next.width = clamp(start.width + dx, MIN_WIDTH, maximumWidth);
    }

    if (interaction.mode.includes("bottom")) {
      const maximumHeight = viewportHeight - start.y - EDGE_GAP;
      next.height = clamp(start.height + dy, MIN_HEIGHT, maximumHeight);
    }

    setBounds(next);
  };

  const startInteraction = (mode, event) => {
    if (event.button !== 0 || (minimized && mode !== "drag")) return;
    event.preventDefault();
    event.stopPropagation();

    interactionRef.current = {
      mode,
      pointerX: event.clientX,
      pointerY: event.clientY,
      bounds,
      moveHandler: handlePointerMove,
      endHandler: stopInteraction,
    };
    document.body.style.userSelect = "none";
    document.body.style.cursor =
      mode === "drag"
        ? "grabbing"
        : mode === "left" || mode === "right"
          ? "ew-resize"
          : mode === "bottom"
            ? "ns-resize"
            : mode === "bottom-left"
              ? "nesw-resize"
              : "nwse-resize";
    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", stopInteraction);
    window.addEventListener("pointercancel", stopInteraction);
  };

  const windowHeight = minimized ? 44 : bounds.height;

  return createPortal(
    <section
      className="parametric-ai-floating-shell"
      aria-label="Parametric AI Agent"
      style={{
        ...shellStyle,
        display: open ? "flex" : "none",
        left: `${bounds.x}px`,
        top: `${bounds.y}px`,
        width: `${bounds.width}px`,
        height: `${windowHeight}px`,
      }}
      onPointerDown={(event) => event.stopPropagation()}
      onClick={(event) => event.stopPropagation()}
    >
      <header
        style={headerStyle}
        onPointerDown={(event) => startInteraction("drag", event)}
      >
        <div style={titleGroupStyle}>
          <span style={agentMarkStyle}>✦</span>
          <span style={onlineDotStyle} />
          <span style={titleStyle}>PCell Agent</span>
        </div>

        <div style={headerActionsStyle}>
          <span style={targetBadgeStyle} title={targetName}>
            {targetName || "No PCell"}
          </span>
          <button
            type="button"
            aria-label={minimized ? "Restore AI Agent" : "Minimize AI Agent"}
            title={minimized ? "Restore" : "Minimize"}
            style={windowButtonStyle}
            onPointerDown={(event) => event.stopPropagation()}
            onClick={() => setMinimized((current) => !current)}
          >
            {minimized ? "□" : "—"}
          </button>
          <button
            type="button"
            aria-label="Close AI Agent"
            title="Close"
            style={windowButtonStyle}
            onPointerDown={(event) => event.stopPropagation()}
            onClick={onClose}
          >
            ×
          </button>
        </div>
      </header>

      {!minimized && <div style={contentStyle}>{children}</div>}

      {!minimized && (
        <>
          <div
            aria-hidden="true"
            style={leftHandleStyle}
            onPointerDown={(event) => startInteraction("left", event)}
          />
          <div
            aria-hidden="true"
            style={rightHandleStyle}
            onPointerDown={(event) => startInteraction("right", event)}
          />
          <div
            aria-hidden="true"
            style={bottomHandleStyle}
            onPointerDown={(event) => startInteraction("bottom", event)}
          />
          <div
            aria-hidden="true"
            style={bottomLeftHandleStyle}
            onPointerDown={(event) => startInteraction("bottom-left", event)}
          />
          <div
            aria-hidden="true"
            style={bottomRightHandleStyle}
            onPointerDown={(event) => startInteraction("bottom-right", event)}
          />
        </>
      )}
    </section>,
    document.body
  );
};

const shellStyle = {
  position: "fixed",
  zIndex: 1800,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  border: "1px solid #315f6a",
  borderRadius: "10px",
  color: "#e8f4f5",
  background: "linear-gradient(150deg, #11191d 0%, #172328 100%)",
  boxShadow:
    "0 24px 60px rgba(0, 0, 0, 0.55), 0 0 0 1px rgba(91, 211, 225, 0.05)",
  fontFamily: "sans-serif",
  boxSizing: "border-box",
};

const headerStyle = {
  height: "44px",
  minHeight: "44px",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "10px",
  padding: "0 8px 0 11px",
  borderBottom: "1px solid rgba(79, 132, 143, 0.38)",
  background: "linear-gradient(180deg, #1b2a30, #152126)",
  cursor: "grab",
  userSelect: "none",
};

const titleGroupStyle = {
  minWidth: 0,
  display: "flex",
  alignItems: "center",
  gap: "8px",
};

const agentMarkStyle = {
  width: "26px",
  height: "26px",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  border: "1px solid #3b7885",
  borderRadius: "7px",
  color: "#79eef8",
  background: "linear-gradient(145deg, #15323a, #10242a)",
  boxShadow: "0 0 12px rgba(85, 216, 230, 0.12)",
  fontSize: "13px",
};

const titleStyle = {
  overflow: "hidden",
  color: "#dff9fb",
  fontSize: "12px",
  fontWeight: 700,
  letterSpacing: "0.1px",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const onlineDotStyle = {
  width: "5px",
  height: "5px",
  borderRadius: "50%",
  background: "#65dfa0",
  boxShadow: "0 0 5px rgba(101, 223, 160, 0.72)",
};

const headerActionsStyle = {
  display: "flex",
  alignItems: "center",
  gap: "5px",
};

const targetBadgeStyle = {
  maxWidth: "110px",
  padding: "3px 7px",
  overflow: "hidden",
  border: "1px solid #315866",
  borderRadius: "999px",
  color: "#aeeef4",
  background: "#10242b",
  fontSize: "9px",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const windowButtonStyle = {
  width: "25px",
  height: "25px",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 0,
  border: "1px solid transparent",
  borderRadius: "5px",
  color: "#a8c0c5",
  background: "transparent",
  fontSize: "15px",
  lineHeight: 1,
  cursor: "pointer",
};

const contentStyle = {
  flex: 1,
  minHeight: 0,
  overflow: "hidden",
  background: "#11191d",
};

const resizeBase = {
  position: "absolute",
  zIndex: 3,
};

const leftHandleStyle = {
  ...resizeBase,
  top: "44px",
  bottom: "10px",
  left: 0,
  width: "7px",
  cursor: "ew-resize",
};

const rightHandleStyle = {
  ...resizeBase,
  top: "44px",
  right: 0,
  bottom: "10px",
  width: "7px",
  cursor: "ew-resize",
};

const bottomHandleStyle = {
  ...resizeBase,
  right: "10px",
  bottom: 0,
  left: "10px",
  height: "7px",
  cursor: "ns-resize",
};

const bottomLeftHandleStyle = {
  ...resizeBase,
  bottom: 0,
  left: 0,
  width: "13px",
  height: "13px",
  cursor: "nesw-resize",
};

const bottomRightHandleStyle = {
  ...resizeBase,
  right: 0,
  bottom: 0,
  width: "13px",
  height: "13px",
  cursor: "nwse-resize",
};

export default FloatingParametricAiWindow;
